import type { ICopiedFrom, IMergedFrom } from "./Contact";
import { BaseEntity, DefaultResponseWithData } from "./system";

export interface IDocumentResponse extends DefaultResponseWithData<IDocument[]> { }

// Mirrors the C# DocumentType enum, which is persisted as ints — only ever append.
export enum DocumentType {
  Vehicle,
  Identity,
  Misc,
  COD,
  CollectionNote,
  Other,
  Yard,
  Part,
  Customer,
  CheckIn,
  // Vehicle photography captured during check-in. Kept apart from Vehicle so it
  // never reaches the eBay listing image picker (GET /vehicle/{id}/images returns
  // Vehicle only) — these record the car as it arrived, not marketing photos.
  CheckInVehicle,
  // Process photography taken during depollution on the yard app. Stored against
  // the vehicle, and kept out of the listing picker and the on-site photo set.
  Depollution,
  // The hazardous waste consignment note photographed on the yard app at check-in
  // (DEFRA waste tracking). Filed against the vehicle's lead; never a listing image.
  WasteConsignmentNote,
  // The registration document filed as a document (the historical ELV import). Names the keeper,
  // so the API redacts it like an Identity document: requiresReveal, opened with the v5c scope.
  V5C
}

export interface IDocument extends BaseEntity {
  fileName: string | null;
  length: number;
  contents: string;
  contentType: string | null;
  userId: string | null;
  timestamp: string | null;
  thumbnail: string | null;
  /**
   * Set on documents held on the CDN instead of as bytes (imported vehicle images). These have no
   * `thumbnail`, so render `documentThumbnailSrc` rather than reading `thumbnail` directly.
   */
  url?: string | null;
  thumbnailUrl?: string | null;
  type: DocumentType;
  uploadedByCustomer: boolean;
  checkInItemId?: string | null;
  /**
   * The document hangs off a check-in step an administrator marked sensitive. The API blanks
   * `thumbnail` and `fileName` on these for everyone, so render a lock rather than an image, and
   * fetch the contents through `viewSensitiveDocument` (scope + PIN) rather than a plain download.
   */
  isSensitive?: boolean;
  /**
   * An identity document with `Documents:ProtectIdentityDocuments` on. Much weaker than
   * `isSensitive`: only the `thumbnail` is withheld, and the ordinary download still works — so
   * render "click to view" and leave the existing preview and download paths alone. Never set at
   * the same time as `isSensitive`.
   */
  requiresReveal?: boolean;
  /**
   * Where the document came from: copied from the contact's documents when the lead was made
   * (reason AutoLink when Core matched the seller on its own), or moved here by a contact merge.
   * Lead documents only.
   */
  copiedFrom?: ICopiedFrom | null;
  mergedFrom?: IMergedFrom | null;
}

/**
 * What to put in an <img src> for a document tile, or null for the placeholder icon. Uploaded
 * documents carry a base64 thumbnail; imported ones carry only CDN urls.
 */
export function documentThumbnailSrc(doc: Pick<IDocument, "thumbnail" | "thumbnailUrl" | "url">): string | null {
  // Always image/png: a PDF's thumbnail is an image, so its own contentType would break the tile.
  if (doc.thumbnail) return `data:image/png;base64,${doc.thumbnail}`;
  return doc.thumbnailUrl || doc.url || null;
}